// src/components/Hermes/Tasks/HermesTaskLogDrawer.tsx
// ============================================
// HermesTaskLogDrawer - 执行日志抽屉（右侧滑出）
//
// 布局：
//   - 全屏遮罩 + 右侧 340px 抽屉（slide-in 动画）
//   - 头部：标题 + 记录数 + 关闭按钮
//   - 过滤器：任务下拉选择 + 清空日志按钮
//   - 列表：每条记录显示 任务名 / 状态 / 开始 / 结束 / 耗时 / 错误信息
// ============================================

import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2 } from "lucide-react";
import { formatDateTime, formatDuration, TASK_CN_NAMES } from "./types";
import type {
  ScheduledTask,
  TaskExecutionRecord,
} from "../../../runtime/SchedulerRuntime/types";

interface Props {
  /** 是否打开 */
  open: boolean;
  /** 当前过滤的任务 ID（undefined = 全部） */
  taskId?: string;
  /** 关闭抽屉 */
  onClose: () => void;
  /** 可选任务列表（用于过滤器下拉） */
  tasks: ScheduledTask[];
  /** 执行历史 */
  history: TaskExecutionRecord[];
  /** 切换过滤任务 */
  onFilterChange: (taskId?: string) => void;
  /** 清空本地展示的日志 */
  onClear: () => void;
}

export default function HermesTaskLogDrawer({
  open,
  taskId,
  onClose,
  tasks,
  history,
  onFilterChange,
  onClear,
}: Props) {
  // 按任务过滤
  const filtered = taskId
    ? history.filter((h) => h.taskId === taskId)
    : history;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* 背景遮罩 */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          {/* 抽屉 */}
          <motion.div
            className="fixed top-0 right-0 h-full w-[340px] z-50 flex flex-col border-l border-white/10"
            style={{
              background:
                "linear-gradient(180deg, #131826 0%, #0a0e1a 100%)",
            }}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", ease: "easeOut", duration: 0.25 }}
          >
            {/* 头部 */}
            <div className="flex items-center justify-between p-3 border-b border-white/5">
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
                <h3 className="text-sm font-medium text-white">执行日志</h3>
                <span className="text-[10px] font-mono text-gray-500">
                  {filtered.length} 条
                </span>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
                title="关闭"
              >
                <X size={16} />
              </button>
            </div>

            {/* 过滤器 */}
            <div className="p-3 border-b border-white/5 flex items-center gap-2">
              <select
                value={taskId || ""}
                onChange={(e) =>
                  onFilterChange(e.target.value || undefined)
                }
                className="flex-1 bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-[11px] font-mono text-gray-200 outline-none focus:border-blue-500/50"
              >
                <option value="">全部任务</option>
                {tasks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {TASK_CN_NAMES[t.id] || t.name}
                  </option>
                ))}
              </select>
              <button
                onClick={onClear}
                className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono text-red-300 transition-colors"
                style={{
                  background: "rgba(248,113,113,0.1)",
                  border: "1px solid rgba(248,113,113,0.25)",
                }}
                title="清空本地日志"
              >
                <Trash2 size={11} /> 清空
              </button>
            </div>

            {/* 日志列表 */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
              {filtered.length === 0 ? (
                <div className="text-center py-8 text-[11px] font-mono text-gray-500">
                  暂无执行记录
                </div>
              ) : (
                filtered.map((h, i) => (
                  <div
                    key={`${h.taskId}-${i}`}
                    className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2"
                  >
                    {/* 任务名 + 状态 */}
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{
                          background: h.success ? "#34D399" : "#F87171",
                        }}
                      />
                      <span className="text-[11px] font-medium text-white truncate">
                        {TASK_CN_NAMES[h.taskId] || h.taskName}
                      </span>
                      <span
                        className="text-[9px] font-mono px-1.5 py-0.5 rounded ml-auto"
                        style={{
                          color: h.success ? "#34D399" : "#F87171",
                          background: h.success
                            ? "rgba(52,211,153,0.1)"
                            : "rgba(248,113,113,0.1)",
                        }}
                      >
                        {h.success ? "成功" : "失败"}
                      </span>
                    </div>
                    {/* 时间 + 耗时 */}
                    <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10px] font-mono text-gray-400">
                      <div>
                        <span className="text-gray-600">开始 </span>
                        {formatDateTime(h.startedAt)}
                      </div>
                      <div>
                        <span className="text-gray-600">耗时 </span>
                        {formatDuration(h.duration)}
                      </div>
                      <div>
                        <span className="text-gray-600">结束 </span>
                        {formatDateTime(h.finishedAt)}
                      </div>
                    </div>
                    {/* 错误信息 */}
                    {!h.success && h.error && (
                      <div className="mt-1 px-1.5 py-1 rounded text-[10px] font-mono text-red-300 bg-red-500/5 border border-red-500/10 break-all">
                        {h.error}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
